import { IconHelp, IconSearch, IconSettings } from "@tabler/icons-react";
import * as React from "react";
import { useLocation } from "react-router";

import { toNavItem } from "@/components/nav-item";
import { NavGroup } from "@/components/nav-group";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import logo from "@/logo.png";
import { usePermissions } from "@/hooks/use-permissions";
import { DASHBOARD_ROUTES, type DashboardRoute } from "@/routes";
import { useAuthStore } from "@/stores/auth.store";

const navSecondary = [
  { title: "Settings", url: "#", icon: IconSettings },
  { title: "Get Help", url: "#", icon: IconHelp },
  { title: "Search", url: "#", icon: IconSearch },
];

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { levelOf } = usePermissions();

  // Areas at "none" stay in the list but go dim and inert — people can see
  // what exists to ask for.
  const itemsFor = React.useCallback(
    (group: DashboardRoute["group"]) =>
      DASHBOARD_ROUTES.filter((route) => route.group === group).map((route) =>
        toNavItem(route, route.area ? levelOf(route.area) === "none" : false),
      ),
    [levelOf],
  );
  const mainItems = itemsFor("main");
  const communityItems = itemsFor("community");
  const backofficeItems = itemsFor("backoffice");

  // The mobile sidebar is an overlay, so it has to get out of the way once a
  // nav item takes you somewhere.
  React.useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [pathname, isMobile, setOpenMobile]);

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:p-1.5!"
            >
              <a href="#">
                <span
                  role="img"
                  aria-label="REMAP.club"
                  className="size-5! shrink-0 bg-orange-500"
                  style={{
                    WebkitMaskImage: `url(${logo})`,
                    maskImage: `url(${logo})`,
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
                <span className="text-base font-semibold">REMAP.club</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={mainItems} />
        <NavGroup label="Community" items={communityItems} />
        <NavGroup label="Backoffice" items={backofficeItems} />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            name: user?.username ?? "",
            email: user?.email ?? "",
            avatar: "",
          }}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
